import {useState, useCallback, useEffect} from 'react';
import LocalStorage from './LocalStorage';

export default function useLocalStorage<T = string>(
    key: string,
    defaultValue?: T,
    options: {
        /**
         * Opt in to sync local storage changes across tabs and windows.
         *
         * Most of the time you don't want to do this as it leads to confusing behaviour and weird bugs.
         *
         * Only real usecase where we want to do this now is dataentry's filter copy/pasting
         *  */
        syncChanges?: boolean;
    } = {syncChanges: false}
) {
    let initialValue = LocalStorage.getItem<T>(key) ?? defaultValue;
    if (typeof initialValue === 'undefined' && typeof defaultValue !== 'undefined') {
        initialValue = defaultValue;
        LocalStorage.setItem(key, defaultValue);
    }

    const [value, setValue] = useState<T | null>(initialValue ?? null);

    const onChange = useCallback((next: T | null) => {
        try {
            if (next == null) {
                LocalStorage.removeItem(key);
            } else {
                LocalStorage.setItem(key, next);
            }
        } catch (e) {
            // setItem can sometimes throw if the storage is full.
            // do nothing as memory state will still be updated
        }
        setValue(next);
    }, []);

    useEffect(() => {
        if (options?.syncChanges) {
            return LocalStorage.listen(() => setValue(LocalStorage.getItem(key)));
        }
    }, [key, options?.syncChanges]);

    return [value, onChange] as const;
}
